import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import { AuthLayout } from "../components/AuthLayout";
import { apiFetch } from "../lib/api";
import { formatDocumentTitle } from "../lib/documentTitle";
import { useTenantIdentity } from "../tenant/tenantIdentity";
import { useSession } from "./session";
import { monogramOf } from "./tenantBranding";
import type { components } from "@clms/contracts";
import {
  activateChoice,
  activeChoice,
  chooseTenant,
  externalChoiceChanged,
  persistChoice,
  pruneChoice,
  settledTenantKey,
  type TenantChoice,
  type TenantMembership,
  type TenantReadStatus,
} from "./tenantSelection";

// ADR-0018 §6 — THE TENANT IS CHOSEN AFTER LOGIN, NEVER BEFORE, and this file is that choice: the read it
// is drawn from, the screen that asks it, and the topbar pill that both shows the answer and changes it.
//
// SINCE 3.11.2026 THAT PILL IS ALSO THE CHROME'S ANSWER TO "whose organisation is this?" for everybody
// else (the owner's decision on task e6daa58b — see `TenantIdentity` below). The two had to become one
// element, because two would have named the same organisation twice in one bar.
//
// WHY AFTER LOGIN. Not usability — a tenant-addressed sign-in page sends that tenant's `kc_idp_hint`
// (pages/LoginPage.tsx, auth/oidc.ts), and an Incedo consultant sent down that path lands in the CUSTOMER's
// own directory, where they have no account and where the realm's `CLMS first broker login` flow refuses
// them (TenantIdpFederation, ADR-0014 §12 C-6). The consultant's door is therefore the TENANT-LESS sign-in
// entry, which sends no hint, and the tenant is chosen once a token exists, from what the token says.
//
// NOTHING ABOUT THE CHOICE CHANGES FOR A PERSON OF ONE TENANT. `GET /api/auth/memberships` answers an empty
// list for them — their roles arrive as client roles in their own tenant and they hold no
// `/tenants/<uuid>/<ROLE>` group — so the gate renders its children untouched, no MENU is offered, and no
// header is added to any request. What they do now see is their organisation's NAME, which is the
// 3.11.2026 decision and not part of §6: it is a label read from their own tenant's settings, it offers
// nothing to switch to, and `tenant-selector` — the switch — is still absent for them.
//
// WHY A BLOCKING SCREEN FOR THE REST, rather than a pill alone. For a person who can be in several tenants
// an unlabelled page is a CORRECTNESS problem and not a cosmetic one: every write they make lands in
// whichever tenant the header happened to name. Rendering the shell first would mean the first page they
// see — and any write they make on it — is in a tenant nobody chose. So the choice is asked once, before
// the shell, and remembered; "my own organisation" is one of its answers and is remembered as such
// (auth/tenantSelection.ts explains why that is a stored value and not an absence).
//
// THE SWITCH IS THE CHOICE. Production addresses tenants by path on one host and the authenticated shell is
// apex-only, so there is no `/t/<slug>/` route to navigate to: changing the choice changes the `X-Tenant`
// the next request carries (src/lib/api.ts). The token is untouched, no authorization request is made, and
// `kc_idp_hint` never enters the picture — which is also why switching RELOADS rather than re-rendering:
// every row on screen was read from the previous tenant, and a shell that kept them while the header moved
// would show one organisation's data under another's name.
//
// WHICH IS ALSO WHY A SWITCH IN ANOTHER TAB RELOADS THIS ONE. `localStorage` is per ORIGIN and the tenants
// share one, so the choice this tab is on can be changed from a tab this tab cannot see. Nothing here reads
// it per request (auth/tenantSelection.ts holds ONE active value per page, which only this page moves), so
// the stale tab keeps routing to the organisation it is displaying — and the external change is adopted the
// one honest way, by replacing the page: the rows, the forms and the label all come back from the tenant
// that is now chosen, rather than the label and the data being left behind by a header that moved.

/** What the picker and the pill read. `status` separates "no memberships" from "could not ask". */
interface TenantSelectionState {
  memberships: readonly TenantMembership[];
  /** The choice in force, or null when the person has not made one yet. */
  choice: TenantChoice | null;
  status: TenantReadStatus;
}

interface TenantSelectionValue extends TenantSelectionState {
  /** Record the first choice (the picker screen). No reload: nothing has been read in another tenant yet. */
  choose: (choice: TenantChoice) => void;
  /**
   * Change it (the topbar pill) — and reload, for the reason in the header comment. Answers FALSE when the
   * choice could not be stored, in which case nothing moved at all: the page keeps showing, and keeps
   * writing to, the organisation it was already in. The caller reports that rather than leaving a label the
   * header does not agree with.
   */
  switchTo: (choice: TenantChoice) => boolean;
}

const TenantSelectionContext = createContext<TenantSelectionValue | null>(null);

/**
 * The caller's own memberships (ADR-0018 §6.2). Authenticated and tenant-agnostic: the route lives in the
 * `/api/auth` group, which is exempt from tenant resolution, because the caller has not chosen a tenant yet.
 */
async function fetchMemberships(): Promise<readonly TenantMembership[]> {
  const body = await apiFetch<components["schemas"]["TenantMembershipsResponse"]>(
    "/api/auth/memberships",
  );
  // Read defensively even though the field is required in the contract: this answer gates the whole shell,
  // and a body that is not what the document promises should be an error the gate handles (below) rather
  // than a `map` of undefined thrown from inside a render.
  return Array.isArray(body.memberships) ? body.memberships : [];
}

/**
 * Holds the memberships and the choice for the whole app. Mounted once, inside the session provider and
 * above the router (src/main.tsx), because both the gate inside the route tree and the pill in the shell
 * read it — and because the read must happen once per signed-in load rather than once per route that
 * happens to render a consumer.
 */
export function TenantSelectionProvider({ children }: { children: ReactNode }) {
  const { session } = useSession();
  // Seeded from the page's ACTIVE choice and not from nothing: that is the tenant every request this page
  // makes is already going to, so it is the only value the pill may be drawn from. A state that started
  // null would be a shell labelled "no organisation" while the header named one.
  const [state, setState] = useState<TenantSelectionState>(() => ({
    memberships: [],
    choice: activeChoice(),
    status: "none",
  }));

  // Keyed on the SUBJECT, so signing in as somebody else on the same browser re-asks rather than inheriting
  // the previous person's answer. A session that ends clears the choice for the same reason.
  const subject = session?.userId ?? null;

  useEffect(() => {
    if (!subject) {
      chooseTenant(null);
      setState({ memberships: [], choice: null, status: "none" });
      return;
    }

    let cancelled = false;
    // A session has appeared. Whatever storage holds for it is this page's tenant from here on — the page
    // may have been signed out a moment ago, which cleared the active value (above), and a session seeded
    // into the same document is how the E2E harness and the email-link redeem both arrive.
    setState((previous) => ({ ...previous, choice: activateChoice(), status: "loading" }));

    fetchMemberships()
      .then((memberships) => {
        if (cancelled) return;
        // `pruneChoice` is what keeps a revoked membership from becoming a 403 on every request, and a
        // renamed customer from being shown under its old name.
        setState({ memberships, choice: pruneChoice(memberships), status: "ready" });
      })
      .catch(() => {
        if (cancelled) return;
        // The read failed — an outage, an aborted load, a 401 the session hook is already handling. NOT a
        // reason to hold the app behind a picker it cannot draw: the person keeps whatever choice they had
        // (the header still travels) and the app renders exactly as it did before this feature existed.
        //
        // What this state is NOT is an answer about which organisation they are in. `memberships: []` here
        // means "could not ask" and not "none" — which is why `status` is kept beside it, and why
        // `settledTenantKey` refuses to settle on this state unless a choice is already in force: read as
        // "no memberships", a failed read would let a tenant-scoped write go out with no `X-Tenant` and
        // land in the tenant the token names, in an organisation nobody chose.
        setState({ memberships: [], choice: activeChoice(), status: "error" });
      });

    return () => {
      cancelled = true;
    };
  }, [subject]);

  // THE DISPLAYED CHOICE IS READ BACK FROM THE ACTIVE ONE, never from the argument. `chooseTenant` moves
  // the value this page routes by and stores it; what it could not do is store it, and a page whose label
  // advanced on its own would be the whole defect this feature exists to prevent. Reading it back makes the
  // two the same value by construction rather than by two call sites agreeing.
  const choose = useCallback((choice: TenantChoice) => {
    chooseTenant(choice);
    setState((previous) => ({ ...previous, choice: activeChoice() }));
  }, []);

  const switchTo = useCallback((choice: TenantChoice) => {
    // STORED FIRST, AND THIS PAGE IS NOT MOVED. The switch is adopted by reloading — every row on screen
    // was read in the tenant being left — so the choice has to survive the reload to mean anything. If the
    // write is refused (a quota, a privacy mode that allows no writes), nothing moves: no reload, no header
    // change, and the pill still names the organisation whose data is on screen. The caller says so.
    if (!persistChoice(choice)) return false;
    // The whole switch: the next request carries the new `X-Tenant`. Home rather than this route, because
    // the route may be one the person has no authority for in the tenant they just entered — and "/" is the
    // learner's own page, which every authenticated session can open.
    window.location.assign("/");
    return true;
  }, []);

  // ANOTHER TAB MOVED THE CHOICE. Reconciled by replacing this page, for the reason in the header comment —
  // and it is safe to take the time a reload takes, because nothing here routes by storage: until the new
  // document is up, this page's requests keep going to the tenant it is still showing.
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (externalChoiceChanged(event)) window.location.reload();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const value = useMemo<TenantSelectionValue>(
    () => ({ ...state, choose, switchTo }),
    [state, choose, switchTo],
  );

  return (
    <TenantSelectionContext.Provider value={value}>{children}</TenantSelectionContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components -- context + hook colocated by design
export function useTenantSelection(): TenantSelectionValue {
  const ctx = useContext(TenantSelectionContext);
  if (!ctx) throw new Error("useTenantSelection must be used within a TenantSelectionProvider");
  return ctx;
}

/**
 * The active choice, or `null` where there is no selection provider at all — which is a real app and not a
 * defensive branch: the OPERATOR CONSOLE (`apps/platform`) mounts no provider, because ADR-0014 A-2 puts
 * the operator in Verso's own tenant and §6's picker is about a person who can be in several customers.
 *
 * It exists for `auth/capabilities`, which both apps share: that hook has to know whether this session
 * reached its tenant by membership (ADR-0018 §4.2), and the throwing hook above would have taken the whole
 * console down with "must be used within a TenantSelectionProvider" the moment it asked. Answering null is
 * the honest answer for the console — its session is always its own tenant's — and it keeps the reason in
 * one named function instead of a try/catch at the call site.
 */
// eslint-disable-next-line react-refresh/only-export-components -- reads the context above it, like useTenantSelection
export function useTenantChoiceIfAny(): TenantChoice | null {
  return useContext(TenantSelectionContext)?.choice ?? null;
}

/**
 * WHICH TENANT this page's requests are going to, as a key — and `null` while that is still an open
 * question, which is the state nothing tenant-scoped may be WRITTEN in.
 *
 * It exists for the work that is not a page: an effect that fires on a signed-in load and writes something
 * per tenant (profile/PreferredLanguageSync.tsx). Such an effect mounted above the choice would run before
 * the person answered the picker, and its write would land in whichever tenant the backend resolved from
 * the token — a tenant nobody chose, which is §6's hazard in the one place the gate below cannot see,
 * because the gate withholds the ROUTER and this work is not in it.
 *
 * The decision itself is `settledTenantKey` in auth/tenantSelection.ts, which is where it is documented and
 * where it is tested: this hook is only the context read in front of it. In particular a read that FAILED
 * is settled only when a choice is already in force — with none, the write would go out with no `X-Tenant`
 * at all and land in the tenant the token names, which is the very write this wait exists to prevent.
 */
// eslint-disable-next-line react-refresh/only-export-components -- reads the context above it, like useTenantSelection
export function useActiveTenantKey(): string | null {
  const { memberships, choice, status } = useTenantSelection();
  return settledTenantKey(status, memberships, choice);
}

/**
 * The gate: for a person with memberships and no choice yet, the picker stands in place of the shell. For
 * everybody else — no memberships, a choice already made, or a read that failed — it renders its children
 * and is invisible.
 *
 * Inside `RequireAuth` (src/app/router.tsx), because the read needs a token; above `AppLayout`, because
 * what it withholds is the whole tenant-scoped surface and not one page of it.
 */
export function RequireTenantSelection({ children }: { children: ReactNode }) {
  const { memberships, choice, status } = useTenantSelection();

  // WHILE THE READ IS IN FLIGHT THE APP RENDERS, and that is a deliberate trade rather than an oversight.
  // Holding the shell back would mean a blank frame on EVERY signed-in load — for the single-tenant
  // majority above all, whose requests go to their own tenant with or without this answer, and whose
  // experience §6.1 promises is unchanged; it would also put one more serial request in front of the first
  // paint, which is the budget `sso-login.realstack.spec.ts` measures. What it costs instead is a
  // multi-tenant person seeing their own tenant's page for the fraction of a second before the picker
  // replaces it.
  //
  // WHICH IS ONLY ACCEPTABLE BECAUSE NOTHING WRITES IN THAT MOMENT, and that is a property of the app
  // rather than of this component. What renders behind the gate renders reads, and a write from the page
  // needs a click — by the time anybody can click, the answer is here. The exception was not a page at
  // all: the sign-in language reconciliation is mounted ABOVE the router, so this gate never saw it, and
  // it POSTs to a per-tenant row the instant a session exists. It now waits for the tenant instead
  // (`useActiveTenantKey`, profile/PreferredLanguageSync.tsx). Anything else that writes on a signed-in
  // load, from outside the router, belongs behind the same wait — the hazard §6 is about is a write
  // landing in a tenant nobody chose, and this gate can only speak for the routes beneath it.
  if (status === "ready" && memberships.length > 0 && choice === null) return <TenantPickerScreen />;
  return <>{children}</>;
}

/**
 * The choice, as a screen. The shape is the one `tenantAccess.tsx` established for a state that stands in
 * place of the router: the `AuthLayout` card, a heading, and the actions — here one button per answer,
 * because that is what the person is being asked.
 */
function TenantPickerScreen() {
  const { t } = useTranslation();
  const { memberships, choose } = useTenantSelection();
  const title = t("tenantPicker.title");
  const appName = t("app.title");

  // The browser tab, written here rather than through the route title channel: this screen stands in place
  // of the router, so no route is matched and the tab would otherwise name the page behind it.
  useEffect(() => {
    document.title = formatDocumentTitle(title, appName);
  }, [title, appName]);

  return (
    <AuthLayout>
      <div className="auth__head">
        <h1 className="auth__title" data-testid="tenant-picker">
          {title}
        </h1>
        <p className="auth__text">{t("tenantPicker.intro")}</p>
      </div>

      <ul className="auth__actions tenant-picker__list" aria-label={title}>
        {memberships.map((membership) => (
          <li key={membership.slug}>
            <button
              type="button"
              className="btn btn--primary btn--block"
              data-testid={`tenant-picker-option-${membership.slug}`}
              onClick={() => choose({ kind: "membership", membership })}
            >
              {membership.name}
            </button>
          </li>
        ))}
        <li>
          {/* The person's OWN organisation — the tenant their token names, which the backend resolves when
              no hint is sent. It is named generically and not by its own display name on purpose: the
              memberships route answers only about memberships (§6.2 — the set is the caller's own
              `tenant_memberships` rows, ADR-0021 §4.1), so this app has no name for the token's own tenant
              and must not invent one. The person
              does not need it named: it is the one organisation on this list that is theirs. */}
          <button
            type="button"
            className="btn btn--ghost btn--block"
            data-testid="tenant-picker-option-own"
            onClick={() => choose({ kind: "own" })}
          >
            {t("tenantPicker.ownOrganisation")}
          </button>
        </li>
      </ul>
    </AuthLayout>
  );
}

/**
 * The organisation's MARK: its logo when one is stored, and the initial of its name when none is.
 *
 * There is always a mark, and that is what makes the narrow top bar legible: below 48rem the name's words
 * are clipped to nothing (styles/shell.css) and this is what is left, so a tenant with no logo must still
 * leave something behind. It is `aria-hidden` in both forms — the name beside it is the accessible content,
 * and the mark would otherwise be announced twice.
 *
 * THE LOGO IS REMOVED FROM THE DOM IF IT FAILS TO LOAD, which is acceptance criterion 2: a stored logo whose
 * object is gone, or a legacy third-party URL that no longer answers, must not leave a broken-image glyph in
 * the chrome. `onError` falls back to the initial, which is the state a tenant with no logo is already in.
 */
function TenantMark({ name, logoUrl }: { name: string; logoUrl: string | null }) {
  // Keyed on the URL so a REPLACED logo gets a fresh attempt: the state below is about one src, and the
  // upload's cache buster (`TenantLogoUrl.Public`) makes a new one on every save.
  const [broken, setBroken] = useState<string | null>(null);
  const usable = logoUrl !== null && broken !== logoUrl;

  return (
    <span className="app-tenant__mark" aria-hidden="true" data-testid="tenant-identity-mark">
      {usable ? (
        <img
          className="app-tenant__logo"
          src={logoUrl}
          alt=""
          data-testid="tenant-identity-logo"
          onError={() => setBroken(logoUrl)}
        />
      ) : (
        monogramOf(name)
      )}
    </span>
  );
}

/**
 * WHICH ORGANISATION this page is — on every page, for every signed-in reader (owner's decision 3.11.2026)
 * — and, for a person whose token carries memberships, the way to change it (ADR-0018 §6).
 *
 * ONE ELEMENT FOR BOTH, which is the decision rather than a convenience. The pill used to be drawn only for
 * a multi-tenant account, and the obvious way to satisfy the decision was to add a label beside it; that
 * label and the pill would then both name the organisation, twice in one bar. So the pill IS the name: a
 * multi-tenant reader gets the menu button they had, a single-tenant reader gets the same element without
 * the menu, and neither can show the name two times.
 *
 * WHERE THE NAME COMES FROM, in order:
 *  1. the organisation's own branding read (tenant/tenantIdentity.ts), which is the only source that knows
 *     the name of a SINGLE-tenant reader's organisation and the only one that carries the logo;
 *  2. the tenant choice, until that read lands — a membership carries its organisation's name
 *     (`GET /api/auth/memberships`), so the pill is correct on the first paint after a choice rather than
 *     blank for a round trip;
 *  3. "my own organisation", for a multi-tenant reader who chose their own tenant and whose read has not
 *     answered. The memberships route answers only about memberships, so there is no name for it there.
 *
 * NOTHING AT ALL is drawn while none of the three has an answer — a single-tenant reader on the very first
 * paint, and a session whose branding read failed with no choice to fall back on. An empty pill would be a
 * statement about an organisation nobody can read.
 *
 * It borrows the profile pill's shape and popup rules (`app-profile*` in styles/shell.css) rather than
 * restating them — the two controls sit side by side and must not drift apart — and adds only what is its
 * own: the mark, a wider label (an organisation's name is longer than a person's), and the `title` that
 * gives the full name back on hover where the bar is too narrow to show it.
 */
export function TenantIdentity() {
  const { t } = useTranslation();
  const { memberships, choice, switchTo, status } = useTenantSelection();
  const branding = useTenantIdentity(useActiveTenantKey());
  const [open, setOpen] = useState(false);
  // The switch that did not happen, because the choice could not be stored. Held here rather than in the
  // provider because this is the control that reports it, and it is said rather than swallowed for one
  // reason: the alternative — applying the switch to the header alone — is a page showing one
  // organisation's data while writing to another, which is the defect, not the lesser evil.
  const [switchFailed, setSwitchFailed] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);

  /** Ask for the switch, and keep the message exactly as long as the switch is still not made. */
  const attemptSwitch = (next: TenantChoice) => setSwitchFailed(!switchTo(next));

  // Same two ways to close as the profile menu beside it: a click outside, and Esc — which hands focus back
  // to the pill so a keyboard user is not dropped at the top of the document.
  useEffect(() => {
    if (!open) return;

    const onPointerDown = (e: PointerEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setOpen(false);
      toggleRef.current?.focus();
    };

    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  // WHETHER THERE IS ANYTHING TO SWITCH BETWEEN. No memberships — or no answer about them — is a person
  // the MENU has nothing to say to; the name below is drawn for them all the same.
  const switchable = status === "ready" && memberships.length > 0;

  const current = branding?.displayName
    ?? (choice?.kind === "membership" ? choice.membership.name : null)
    ?? (switchable ? t("tenantPicker.ownOrganisation") : null);

  // Nothing is known about the organisation yet. See the remarks: an unnamed pill says nothing true.
  if (current === null) return null;

  const mark = <TenantMark name={current} logoUrl={branding?.logoUrl ?? null} />;

  // A reader with one organisation. The same element, minus everything that opens: no button, no menu, no
  // `aria-haspopup` claiming something will happen.
  //
  // WHAT SAYS THE NAME IS AN ORGANISATION is a visually-hidden word, not an `aria-label`: this is a <div>
  // and a <span> with no role, and `aria-label` on a generic element is not reliably exposed — it would
  // have looked right in the source and said nothing to a screen reader. Hidden text is read in document
  // order, so the bar announces "Organisaatio: Metsäkoulu Oy" where the switchable pill's own `aria-label`
  // says the same thing. The `title` is for the narrow bar, where the words are clipped to nothing and the
  // mark is all that shows.
  if (!switchable) {
    return (
      <div
        className="app-topbar__tenant app-tenant-plain"
        title={current}
        data-testid="tenant-identity"
      >
        {mark}
        <span className="visually-hidden">{t("tenantPicker.subjectLabel")}</span>
        <span className="app-profile__name app-tenant__name" data-testid="tenant-selector-current">
          {current}
        </span>
      </div>
    );
  }

  return (
    <div className="app-topbar__profile app-topbar__tenant" ref={containerRef} data-testid="tenant-identity">
      <button
        ref={toggleRef}
        type="button"
        className="app-profile app-tenant"
        id="topbar-tenant"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls="topbar-tenant-menu"
        // The label names the subject, because the pill's own text is a value and not a question: a screen
        // reader otherwise announces an organisation's name with nothing saying what it is. It also carries
        // the whole name where the bar clips it, which the `title` beside it does for a sighted reader.
        aria-label={t("tenantPicker.currentLabel", { tenant: current })}
        title={current}
        onClick={() => setOpen((previous) => !previous)}
        data-testid="tenant-selector"
      >
        {mark}
        <span className="app-profile__name app-tenant__name" data-testid="tenant-selector-current">
          {current}
        </span>
        <svg
          className="app-profile__chevrons"
          width="12"
          height="18"
          viewBox="0 0 12 18"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          focusable="false"
        >
          <path d="M2.5 7.2 6 3.7l3.5 3.5" />
          <path d="M2.5 10.8 6 14.3l3.5-3.5" />
        </svg>
      </button>

      {open && (
        <div
          className="app-profile-menu app-tenant-menu"
          id="topbar-tenant-menu"
          role="menu"
          aria-labelledby="topbar-tenant"
        >
          {memberships.map((membership) => (
            <button
              key={membership.slug}
              role="menuitem"
              type="button"
              // `aria-current` and not a tick: the selected row is a statement about where the reader IS,
              // which is the whole point of this control.
              aria-current={
                choice?.kind === "membership" && choice.membership.slug === membership.slug
                  ? "true"
                  : undefined
              }
              data-testid={`tenant-selector-option-${membership.slug}`}
              onClick={() => attemptSwitch({ kind: "membership", membership })}
            >
              {membership.name}
            </button>
          ))}
          <span className="app-profile-menu__sep" aria-hidden="true" />
          <button
            role="menuitem"
            type="button"
            aria-current={choice?.kind === "own" ? "true" : undefined}
            data-testid="tenant-selector-option-own"
            onClick={() => attemptSwitch({ kind: "own" })}
          >
            {t("tenantPicker.ownOrganisation")}
          </button>
          {switchFailed && (
            // The switch did not happen, and the pill above still names the organisation this page is in —
            // which is the point of saying it here, inside the control that was pressed, rather than
            // anywhere that could read as a property of the page's content.
            <p className="app-tenant-menu__alert" role="alert" data-testid="tenant-selector-switch-failed">
              {t("tenantPicker.switchFailed")}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
